import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { subscriptionAPI, llorosAPI } from '../services/api';
import { SubscriptionPlan } from '../types';

interface SubscriptionContextType {
  plan: SubscriptionPlan;
  isPremium: boolean;
  isTrial: boolean;
  isFree: boolean;
  trialDaysRemaining: number;
  canUse: (feature: string) => boolean;
  refreshSubscription: () => Promise<void>;
  /** false mientras el modelo freemium este desactivado. Sirve para ocultar
   *  cualquier UI que hable de planes, precios o mejoras de suscripcion. */
  subscriptionsEnabled: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

// ---------------------------------------------------------------------------
// INTERRUPTOR DEL SISTEMA DE SUSCRIPCIONES (debe coincidir con el del backend,
// en backend/models/Subscription.js).
//
// false = freemium desactivado: todas las funcionalidades abiertas para todos.
//         Con esto desaparecen solos los candados de la navegacion, los
//         UpgradePrompt y el banner de trial, porque todos cuelgan de canUse()
//         y de isTrial.
// true  = se recupera el gating por plan.
//
// El acceso a "lloros" es independiente: va por usuario, no por plan.
// ---------------------------------------------------------------------------
const SUBSCRIPTIONS_ENABLED = false;

// Features disponibles por plan (lloros NO esta aqui, se controla por usuario)
const PLAN_FEATURES: Record<SubscriptionPlan, Record<string, boolean>> = {
  free: {
    basic_tracking: true,
    calendar_view: true,
    tensions: false,
    reminders: false,
    advanced_analytics: false,
    export_data: false,
  },
  trial: {
    basic_tracking: true,
    calendar_view: true,
    tensions: true,
    reminders: true,
    advanced_analytics: true,
    export_data: true,
  },
  premium: {
    basic_tracking: true,
    calendar_view: true,
    tensions: true,
    reminders: true,
    advanced_analytics: true,
    export_data: true,
  },
};

export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [plan, setPlan] = useState<SubscriptionPlan>((user?.subscription_plan as SubscriptionPlan) || 'free');
  const [trialDaysRemaining, setTrialDaysRemaining] = useState<number>(user?.trial_days_remaining || 0);
  const [canUseLloros, setCanUseLloros] = useState<boolean>(false);

  // Con el freemium desactivado todo el mundo cuenta como premium: asi el banner
  // de trial y los avisos de "actualiza tu plan" no aparecen en ningun sitio.
  const isPremium = !SUBSCRIPTIONS_ENABLED || plan === 'premium' || plan === 'trial';
  const isTrial = SUBSCRIPTIONS_ENABLED && plan === 'trial';
  const isFree = SUBSCRIPTIONS_ENABLED && plan === 'free';

  // Sincronizar con datos del user cuando cambia
  useEffect(() => {
    if (user) {
      setPlan((user.subscription_plan as SubscriptionPlan) || 'free');
      setTrialDaysRemaining(user.trial_days_remaining || 0);
    }
  }, [user]);

  // Verificar acceso a lloros (controlado por usuario, no por plan)
  const checkLlorosAccess = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await llorosAPI.canAccess();
      setCanUseLloros(response.data?.canAccess === true);
    } catch {
      setCanUseLloros(false);
    }
  }, [isAuthenticated]);

  // Refrescar desde API al montar (si autenticado)
  const refreshSubscription = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await subscriptionAPI.getTrialInfo();
      if (response.success && response.data) {
        const data = response.data;
        setPlan(data.current_plan as SubscriptionPlan);
        setTrialDaysRemaining(data.trial_days_remaining || 0);
      }
    } catch (error) {
      // Silently fail - use cached user data
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      refreshSubscription();
      checkLlorosAccess();
    }
  }, [isAuthenticated, refreshSubscription, checkLlorosAccess]);

  const canUse = useCallback((feature: string): boolean => {
    // Lloros tiene control de acceso por usuario, no por plan: se comprueba
    // siempre contra el backend, tambien con el freemium desactivado.
    if (feature === 'lloros') return canUseLloros;
    // Freemium desactivado: el resto de funcionalidades estan abiertas
    if (!SUBSCRIPTIONS_ENABLED) return true;
    const features = PLAN_FEATURES[plan] || PLAN_FEATURES.free;
    return features[feature] === true;
  }, [plan, canUseLloros]);

  return (
    <SubscriptionContext.Provider value={{
      plan,
      isPremium,
      isTrial,
      isFree,
      trialDaysRemaining,
      canUse,
      refreshSubscription,
      subscriptionsEnabled: SUBSCRIPTIONS_ENABLED,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
